import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';

import { I18nService } from '@recruitee/i18n';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../core/overlay/overlay-directives';
import { ConnectionPositionPair } from '../core/overlay/position/connected-position';
import { InputComponent } from '../forms/input/input.component';
import { RtStatefulComponent } from '../utils/rt-stateful-component';
import {
  InputSelectDropdownOption,
  InputWithSelectDropdownComponent,
} from './input-with-select-dropdown/input-with-select-dropdown.component';

type State = {
  dropdownOpen: boolean;
};

@Component({
  selector: 'rt-input-with-select',
  templateUrl: './input-with-select.component.html',
  styleUrls: ['./input-with-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ConnectedOverlayDirective,
    InputComponent,
    InputWithSelectDropdownComponent,
    NgIf,
    OverlayOriginDirective,
    ReactiveFormsModule,
  ],
})
export class InputWithSelectComponent extends RtStatefulComponent<State> {
  @Input() public dropdownMaxHeight: number = 300;
  @Input() public dropdownIconHasMargin: boolean = true;
  @Input() public value: string | number | null = null;
  @Input() public options: InputSelectDropdownOption[] = [];
  @Input() public translationKey: string;
  @Input() public noLeftPadding: boolean = false;
  @Input() public placeholder: string = '';
  @Input() public invalid: boolean;

  @Output() public valueChanges: EventEmitter<string | null | number> = new EventEmitter();

  public dropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
  ];
  public dropdownWidth: number = 132;
  public valueControl: UntypedFormControl;
  public currentOption: InputSelectDropdownOption | null;

  constructor(protected cdRef: ChangeDetectorRef, private i18nService: I18nService) {
    super(cdRef);
    this.createState({
      dropdownOpen: false,
    });
  }

  public ngOnInit(): void {
    if (this.value !== null) {
      this.currentOption = this.getOptionFromValue(this.value);
    }

    this.valueControl = new UntypedFormControl(this.currentOption?.value, [
      Validators.pattern(/^[0-9]*$/),
    ]);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value && this.value !== null && this.valueControl) {
      this.currentOption = this.getOptionFromValue(this.value);
      this.valueControl.setValue(this.currentOption?.value, { emitEvent: false });
    }
  }

  public onInputChange(value: string): void {
    this.currentOption = this.getOptionFromValue(value);
    this.valueChanges.emit(this.currentOption?.value);
  }

  public onValueSelect(option: InputSelectDropdownOption): void {
    this.valueControl.setValue(option.value);
    this.currentOption = option;
    this.valueChanges.emit(option.value);
  }

  public toggleDropdown(state: boolean | undefined = undefined): void {
    this.setState({
      dropdownOpen: state === undefined ? !this.state.dropdownOpen : state,
    });
  }

  private getOptionFromValue(value: string | number | null): InputSelectDropdownOption | null {
    if (value === null) return null;

    const parsedValue = parseInt(<string>value, 10);
    if (Number.isNaN(parsedValue)) return { label: `${value}`, value };

    return {
      value: parsedValue,
      label: this.i18nService.translate(this.translationKey, { count: parsedValue }),
    };
  }
}
